'use client';

import { useEffect, useMemo, useState } from 'react';
import { Avatar, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUsers } from '@fortawesome/free-solid-svg-icons';

const AA_LIGHTNESS = [42, 33, 33, 35, 36, 35, 35, 47, 57, 50, 50, 52];

const hueToPalette = (hue: number): { bg: string; fg: string } => ({
  bg: `hsl(${hue}, 45%, ${AA_LIGHTNESS[Math.floor(hue / 30)]}%)`,
  fg: '#FFFFFF',
});

export const paletteForSeed = (seed: string): { bg: string; fg: string } => {
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 33) ^ seed.charCodeAt(i);
  }
  const hue = Math.round((235 + Math.abs(hash) * 137.508) % 360) % 360;
  return hueToPalette(hue);
};

// Ordenado para que slots consecutivos estén lo más lejos posible en tono:
// los responsables se asignan por índice según id ordenado, así dos ids
// contiguos nunca caen en colores parecidos. (~130° de separación mínima).
const RESPONSIBLE_PALETTE: ReadonlyArray<readonly [number, number]> = [
  [0, 42],
  [200, 26],
  [20, 28],
  [210, 47],
  [20, 42],
  [235, 33],
  [40, 33],
  [245, 57],
  [75, 26],
  [280, 36],
  [120, 36],
  [290, 50],
  [160, 35],
  [325, 36],
  [175, 26],
  [340, 52],
];

export const paletteForIndex = (index: number): { bg: string; fg: string } => {
  const n = RESPONSIBLE_PALETTE.length;
  const i = ((index % n) + n) % n;
  const octave = Math.max(0, Math.floor(index / n));
  const [hue, baseLightness] = RESPONSIBLE_PALETTE[i];
  const lightness = Math.max(baseLightness - 8 * octave, 20);
  return { bg: `hsl(${hue}, 45%, ${lightness}%)`, fg: '#FFFFFF' };
};

/**
 * Deriva iniciales de un nombre. Devuelve null cuando no hay un nombre real del
 * que extraerlas (vacío, número de teléfono o el sentinel "Cliente anónimo"),
 * para que el llamador caiga al ícono de persona en su lugar.
 */
const getInitials = (name?: string): string | null => {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  // Solo dígitos / símbolos de teléfono → las iniciales no aportan nada.
  if (/^[+\d\s().-]+$/.test(trimmed)) return null;
  if (trimmed.toLowerCase() === 'cliente anónimo') return null;

  const words = trimmed.split(/\s+/).filter(Boolean);
  const letters = words
    .slice(0, 2)
    .map((word) => [...word][0] ?? '')
    .join('');
  return letters ? letters.toUpperCase() : null;
};

/**
 * Las URLs de foto de WhatsApp (pps.whatsapp.net) van firmadas con un token
 * `oh=` de vida corta y empiezan a dar 403 a los pocos minutos, así que ni
 * siquiera las pedimos: renderizamos iniciales. Cualquier otro host (p. ej. un
 * avatar proxeado/cacheado por el backend en el futuro) sí se renderiza.
 */
export const isRenderableAvatarUrl = (url?: string | null): boolean => {
  if (!url) return false;
  return !/whatsapp\.net/i.test(url);
};

export interface ContactAvatarProps {
  /** Nombre a mostrar resuelto (puede ser un teléfono; se filtra internamente). */
  readonly name?: string;
  /** Identificador estable para el hash de color (wid/teléfono/id). */
  readonly seed?: string;
  /** URL de la foto del contacto, si existe. */
  readonly imageUrl?: string;
  /** Si el chat es un grupo, se muestra un ícono de grupo. */
  readonly isGroup?: boolean;
  /** Tamaño del avatar en píxeles. */
  readonly size: number;
  readonly palette?: { bg: string; fg: string };
}

/**
 * Avatar de contacto con degradación elegante: foto → iniciales con color
 * determinista → ícono de persona/grupo. Reemplaza al placeholder genérico que
 * no aportaba información para distinguir un chat de otro.
 */
const ContactAvatar = ({
  name,
  seed,
  imageUrl,
  isGroup,
  size,
  palette: paletteProp,
}: ContactAvatarProps) => {
  const [imageError, setImageError] = useState(false);

  // Reintentar la imagen cuando cambia la URL (p. ej. el backend la refresca).
  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  const initials = useMemo(() => getInitials(name), [name]);

  const hashedPalette = useMemo(
    () => paletteForSeed(seed || name || initials || 'anon'),
    [seed, name, initials]
  );
  const palette = paletteProp ?? hashedPalette;

  const sharedStyle = useMemo(
    () => ({
      width: size,
      height: size,
      borderRadius: '50%',
    }),
    [size]
  );

  // 1) Foto real (solo si la URL es renderable y no ha fallado).
  if (!isGroup && isRenderableAvatarUrl(imageUrl) && !imageError) {
    return (
      <Tooltip title={name}>
        <Avatar
          style={{ ...sharedStyle, objectFit: 'cover' }}
          src={imageUrl}
          onError={() => {
            setImageError(true);
            return false;
          }}
        />
      </Tooltip>
    );
  }

  // 2) Grupo o contacto sin nombre → ícono (contorno).
  if (isGroup || !initials) {
    return (
      <Tooltip title={name}>
        <Avatar
          style={{
            ...sharedStyle,
            backgroundColor: palette.bg,
            color: palette.fg,
          }}
          icon={
            <FontAwesomeIcon
              icon={isGroup ? faUsers : faUser}
              style={{ fontSize: size * 0.45 }}
            />
          }
        />
      </Tooltip>
    );
  }

  // 3) Iniciales con color determinista (contorno).
  return (
    <Tooltip title={name}>
      <Avatar
        style={{
          ...sharedStyle,
          backgroundColor: palette.bg,
          color: palette.fg,
          fontSize: size * (initials.length > 1 ? 0.38 : 0.45),
          fontWeight: 600,
        }}
      >
        {initials}
      </Avatar>
    </Tooltip>
  );
};

export default ContactAvatar;
